import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import {
  createPublicTakeawayPayment,
  forgetTrackedTakeawayOrder,
  getPublicTakeawayOrder,
  getTrackedTakeawayOrderIds,
  takeawayTrackingKey,
  verifyPublicTakeawayPayment,
} from "./takeawayApi";
import { openTakeawayRazorpay } from "./takeawayPayment";
import { useRazorpay } from "../../component/Payment/Razorpay";
import { toast } from "react-toastify";
import { formatIndiaDateTime } from "../../Utills/takeawayTime";
import "./takeaway.scss";

const steps = ["Order received", "Accepted", "Preparing", "Ready", "Collected"];
const closedStatuses = new Set([4, 5, 6]);

const paymentText = (order) => order.paymentStatus === 1 ? "Paid"
  : order.paymentStatus === 2 ? "Refund pending"
    : order.paymentStatus === 3 ? "Refunded"
      : order.paymentMode === "Online" ? "Online payment pending"
        : order.fulfillmentType === 1 ? "Pay at restaurant" : "Pay at pickup";

const TakeawayOrderTrackingCard = ({ order, paying, razorpayLoaded, onPay }) => {
  const cancelled = order.status === 5 || order.status === 6;
  return <article className="takeaway-tracking-order">
    <header>
      <div><span>Pickup token</span><h2>{order.tokenNumber}</h2></div>
      <strong>{cancelled ? (order.status === 6 ? "Order rejected" : "Order cancelled") : steps[order.status]}</strong>
    </header>
    {!cancelled && <div className="takeaway-progress">{steps.map((step, index) => <div key={step} className={index <= order.status ? "active" : ""}><i>{index + 1}</i><span>{step}</span></div>)}</div>}
    <div className="takeaway-tracked-items">
      {(order.items || []).map((item) => <div key={item.takeawayOrderItemId || `${item.menuId}-${item.itemName}`}>
        <span><b>{item.quantity} × {item.itemName}</b>{item.addonSummary && <small>{item.addonSummary}</small>}{item.note && <small>Note: {item.note}</small>}</span>
        <strong>₹{Number(item.lineTotal).toFixed(2)}</strong>
      </div>)}
    </div>
    <div className="takeaway-status-card">
      <span>Order type</span><strong>{order.fulfillmentType === 1 ? "Dine-in" : "Pick-up"}</strong>
      <span>Total</span><strong>₹{Number(order.grandTotal).toFixed(2)}</strong>
      <span>Payment</span><strong>{paymentText(order)}</strong>
      {order.pickupTime && <><span>Pickup time</span><strong>{formatIndiaDateTime(order.pickupTime)} IST</strong></>}
    </div>
    {order.paymentMode === "Online" && order.paymentStatus !== 1 && !cancelled && <button className="takeaway-pay-button" disabled={!razorpayLoaded || paying} onClick={() => onPay(order)}>{paying ? "Opening payment..." : "Pay online"}</button>}
  </article>;
};

const TakeawayStatus = () => {
  const { restaurant, id, orderId } = useParams();
  const location = useLocation();
  const isOrderDetail = Boolean(orderId);
  const [orders, setOrders] = useState(location.state?.order ? [location.state.order] : []);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [payingOrderId, setPayingOrderId] = useState(null);
  const razorpayLoaded = useRazorpay();

  const load = useCallback(async () => {
    const orderIds = isOrderDetail ? [orderId] : getTrackedTakeawayOrderIds(id);
    if (!orderIds.length) {
      setOrders([]);
      setError("");
      setLoading(false);
      return;
    }

    const results = await Promise.allSettled(orderIds.map(async (trackedOrderId) => {
      const token = localStorage.getItem(takeawayTrackingKey(id, trackedOrderId));
      if (!token) throw Object.assign(new Error("Tracking token is missing."), { status: 404, orderId: trackedOrderId });
      try { return await getPublicTakeawayOrder(id, trackedOrderId, token); }
      catch (requestError) { requestError.orderId = trackedOrderId; throw requestError; }
    }));

    const loaded = [];
    let temporaryError = "";
    results.forEach((result) => {
      if (result.status === "fulfilled") {
        if (!isOrderDetail && closedStatuses.has(result.value.status)) {
          forgetTrackedTakeawayOrder(id, result.value.takeawayOrderId);
        } else {
          loaded.push(result.value);
        }
      } else if (result.reason?.status === 404) {
        if (!isOrderDetail) forgetTrackedTakeawayOrder(id, result.reason.orderId);
        else temporaryError = "Order tracking access is invalid.";
      } else {
        temporaryError = result.reason?.message || "Orders could not be refreshed.";
      }
    });

    loaded.sort((left, right) => new Date(right.createdAt) - new Date(left.createdAt));
    setOrders(loaded);
    setError(temporaryError);
    setLoading(false);
  }, [id, isOrderDetail, orderId]);

  useEffect(() => {
    load();
    const timer = setInterval(load, 15000);
    return () => clearInterval(timer);
  }, [load]);

  const payOnline = async (order) => {
    const token = localStorage.getItem(takeawayTrackingKey(id, order.takeawayOrderId));
    setPayingOrderId(order.takeawayOrderId);
    try {
      const gateway = await createPublicTakeawayPayment(id, order.takeawayOrderId, token);
      openTakeawayRazorpay({
        gateway, restaurantName: restaurant, order,
        onSuccess: async (response) => {
          try {
            const paidOrder = await verifyPublicTakeawayPayment(id, order.takeawayOrderId, token, {
              providerOrderId: response.razorpay_order_id,
              providerPaymentId: response.razorpay_payment_id,
              signature: response.razorpay_signature,
            });
            setOrders((current) => current.map((item) => item.takeawayOrderId === paidOrder.takeawayOrderId ? paidOrder : item));
            toast.success("Payment completed.");
          } catch (paymentError) { toast.error(paymentError.message); }
          finally { setPayingOrderId(null); }
        },
        onDismiss: () => setPayingOrderId(null),
        onFailure: () => { toast.error("Payment failed. Please try again."); setPayingOrderId(null); },
      });
    } catch (paymentError) { toast.error(paymentError.message); setPayingOrderId(null); }
  };

  if (loading && !orders.length) return <div className="public-takeaway-state">Loading orders...</div>;
  if (isOrderDetail && error && !orders.length) return <div className="public-takeaway-state"><h1>Track takeaway</h1><p>{error}</p><Link className="takeaway-link-button" to={`/${restaurant}/${id}/takeaway/menu`}>Open menu</Link></div>;

  return <main className="takeaway-status-list-page">
    <header className="takeaway-status-list-header"><div><p>Takeaway tracking</p><h1>{isOrderDetail ? "Order status" : "Your active orders"}</h1></div><Link className="takeaway-link-button" to={`/${restaurant}/${id}/takeaway/menu`}>Order more</Link></header>
    {error && <p className="takeaway-tracking-error">{error}</p>}
    {!orders.length ? <div className="public-takeaway-state"><h2>No active takeaway orders</h2><p>New, accepted, preparing and ready orders placed on this device will appear here.</p></div>
      : <div className="takeaway-tracking-orders">{orders.map((order) => <TakeawayOrderTrackingCard key={order.takeawayOrderId} order={order} paying={payingOrderId === order.takeawayOrderId} razorpayLoaded={razorpayLoaded} onPay={payOnline} />)}</div>}
    <p className="takeaway-refresh-note">Status refreshes automatically every 15 seconds.</p>
  </main>;
};

export default TakeawayStatus;
